import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface MenuLanguage {
  name: string
  value: string
  default: number
  selected: number
}

export interface MenuLanguageResponse {
  error: boolean
  message: string
  data: MenuLanguage[]
}

export const useMenuLanguages = (getRestaurantId: () => string) => {
  const menuLanguages = ref<MenuLanguage[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Fetch menu languages for a restaurant from the backend
   */
  const fetchMenuLanguages = async (): Promise<void> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      error.value = 'Restaurant ID is required to fetch menu languages'
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await apiInstance.get<MenuLanguageResponse>(
        `/restaurant/${restaurantId}/menu-language`
      )

      if (response.data.error === false && response.data.data) {
        menuLanguages.value = response.data.data
      } else {
        error.value = response.data.message || 'Failed to fetch menu languages'
        menuLanguages.value = []
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to fetch menu languages'
      menuLanguages.value = []
      console.error('Error fetching menu languages:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update language selection status
   */
  const updateLanguageSelection = async (langValue: string, status: number): Promise<boolean> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      error.value = 'Restaurant ID is required to update language selection'
      return false
    }

    try {
      const response = await apiInstance.post<MenuLanguageResponse>(
        `/restaurant/${restaurantId}/menu-language`,
        { lang: langValue, status }
      )

      if (!response.data.error) {
        // Update the language in the local state
        const languageIndex = menuLanguages.value.findIndex(lang => lang.value === langValue)
        if (languageIndex !== -1) {
          menuLanguages.value[languageIndex].selected = status
        }
        return true
      } else {
        error.value = response.data.message || 'Failed to update language selection'
        return false
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to update language selection'
      console.error('Error updating language selection:', err)
      return false
    }
  }

  /**
   * Get selected languages
   */
  const getSelectedLanguages = (): string[] => {
    return menuLanguages.value
      .filter(lang => lang.selected === 1)
      .map(lang => lang.value)
  }

  /**
   * Get all available language codes
   */
  const getAvailableLanguageCodes = (): string[] => {
    return menuLanguages.value.map(lang => lang.value)
  }

  /**
   * Check if a language is default
   */
  const isLanguageDefault = (langValue: string): boolean => {
    const language = menuLanguages.value.find(lang => lang.value === langValue)
    return language ? language.default === 1 : false
  }

  /**
   * Check if a language is selected
   */
  const isLanguageSelected = (langValue: string): boolean => {
    const language = menuLanguages.value.find(lang => lang.value === langValue)
    return language ? language.selected === 1 : false
  }

  /**
   * Reset menu languages
   */
  const reset = () => {
    menuLanguages.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    menuLanguages,
    isLoading,
    error,

    // Methods
    fetchMenuLanguages,
    updateLanguageSelection,
    getSelectedLanguages,
    getAvailableLanguageCodes,
    isLanguageDefault,
    isLanguageSelected,
    reset,
  }
}
